/**
 * Frontend Sentry integration.
 *
 * This module is the only frontend owner of the Sentry SDK. Keep initialization,
 * privacy controls, authenticated context, and route context here so feature
 * components never need to configure or import the SDK directly.
 * just updating something here. 
 */
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  ErrorBoundary as SentryErrorBoundary,
  addIntegration,
  breadcrumbsIntegration,
  captureReactException,
  init as initSentryClient,
  reactRouterV6BrowserTracingIntegration,
  setTag,
  setUser as setSentryUserContext,
  withScope,
  withSentryReactRouterV6Routing as wrapSentryReactRouterV6Routing,
} from "@sentry/react";
import {
  createRoutesFromChildren,
  matchPath,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { getModuleRouteDefinitions } from "modules/appModuleRoutes";

const SENSITIVE_FIELD_NAMES =
  "authorization|cookie|password|secret|token|access_token|refresh_token|api_key|apikey|api-key";
const SENSITIVE_KEY_PATTERN = new RegExp(`(?:${SENSITIVE_FIELD_NAMES})`, "i");
const SENSITIVE_QUERY_PARAMETER_PATTERN = new RegExp(`^(?:${SENSITIVE_FIELD_NAMES})$`, "i");
const SENSITIVE_KEY_VALUE_PATTERN = new RegExp(
  String.raw`((${SENSITIVE_FIELD_NAMES})\s*[=:]\s*)(?:Bearer\s+)?[^\s,;&]+`,
  "gi",
);
const BEARER_TOKEN_PATTERN = /Bearer\s+[^\s,;&]+/gi;
const FILTERED_VALUE = "[Filtered]";
const NON_MODULE_ROUTE_DEFINITIONS = Object.freeze([
  { path: "/", routeId: "root" },
  { path: "/login", routeId: "login" },
  { path: "/external/upload", routeId: "external.upload" },
  {
    path: "/session/obligation/:token",
    routeId: "session.obligation",
  },
  { path: "/required/documents/:sessionId", routeId: "external.required-documents" },
  {
    path: "/supplier/required/documents/:sessionId",
    routeId: "external.supplier-required-documents",
  },
  { path: "/external/actions/login", routeId: "external.actions-login" },
  { path: "/terms-and-conditions", routeId: "terms-and-conditions" },
  { path: "/account/misconfigured", routeId: "account.misconfigured" },
  { path: "/test", routeId: "test" },
  { path: "*", routeId: "not-found" },
]);

let isInitialized = false;
let replayIntegrationLoadPromise = null;

function getTrimmedString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function getLocationOrigin() {
  return globalThis.window?.location?.origin;
}

function redactSensitiveData(value, seen = new WeakMap()) {
  if (typeof value === "string") {
    return value
      .replace(SENSITIVE_KEY_VALUE_PATTERN, `$1${FILTERED_VALUE}`)
      .replace(BEARER_TOKEN_PATTERN, `Bearer ${FILTERED_VALUE}`);
  }
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return seen.get(value);

  if (Array.isArray(value)) {
    const sanitized = [];
    seen.set(value, sanitized);
    value.forEach((item) => sanitized.push(redactSensitiveData(item, seen)));
    return sanitized;
  }

  const sanitized = {};
  seen.set(value, sanitized);
  Object.entries(value).forEach(([key, item]) => {
    sanitized[key] = SENSITIVE_KEY_PATTERN.test(key)
      ? FILTERED_VALUE
      : redactSensitiveData(item, seen);
  });
  return sanitized;
}

function redactSensitiveQueryParameters(url) {
  if (typeof url !== "string" || !url) return url;

  try {
    const parsed = new URL(url, getLocationOrigin() || "http://localhost");
    parsed.searchParams.forEach((_, key) => {
      if (SENSITIVE_QUERY_PARAMETER_PATTERN.test(key)) {
        parsed.searchParams.set(key, FILTERED_VALUE);
      }
    });

    return /^https?:\/\//i.test(url)
      ? parsed.toString()
      : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

/** Exported for focused privacy regression tests. */
export function sanitizeSentryEvent(event) {
  const sanitized = redactSensitiveData(event);

  if (sanitized.user) {
    sanitized.user = sanitized.user.id ? { id: sanitized.user.id } : undefined;
  }
  if (sanitized.request) {
    sanitized.request = {
      ...sanitized.request,
      cookies: undefined,
      data: undefined,
      url: redactSensitiveQueryParameters(sanitized.request.url),
    };
  }

  return sanitized;
}

/** Exported for focused privacy regression tests. */
export function sanitizeSentryBreadcrumb(breadcrumb) {
  const sanitized = redactSensitiveData(breadcrumb);
  if (sanitized?.data?.url) {
    sanitized.data.url = redactSensitiveQueryParameters(sanitized.data.url);
  }
  return sanitized;
}

function routeSpecificity(path) {
  return path.split("/").reduce((score, segment) => {
    if (!segment || segment === "*") return score;
    return score + (segment.startsWith(":") ? 2 : 3);
  }, 0);
}

function getSentryRouteDefinitions() {
  return [...getModuleRouteDefinitions(), ...NON_MODULE_ROUTE_DEFINITIONS];
}

/**
 * Resolve a browser pathname to the stable registry route pattern used for
 * telemetry. Dynamic IDs are never included in Sentry tags.
 */
export function resolveModuleForPathname(
  pathname,
  routeDefinitions = getSentryRouteDefinitions(),
) {
  if (typeof pathname !== "string" || !pathname.startsWith("/")) return undefined;

  return routeDefinitions
    .filter((definition) => matchPath({ path: definition.path, end: true }, pathname))
    .sort((left, right) => routeSpecificity(right.path) - routeSpecificity(left.path))[0];
}

/** Apply only stable, non-PII route tags to future Sentry events. */
export function setSentryRouteContext(pathname, routeDefinitions) {
  const route = resolveModuleForPathname(pathname, routeDefinitions);

  setTag("route", route?.path || "unmatched");
  setTag("module", route?.moduleKey);
  setTag("routeId", route?.routeId);
}

/** Keep Router context synchronization at the top of the route tree. */
export function SentryRouteContext() {
  const location = useLocation();

  useEffect(() => {
    setSentryRouteContext(location.pathname);
  }, [location.pathname]);

  return null;
}

/** Initialize once before React renders. Safe to call repeatedly. */
export function initSentry(
  environment = process.env,
  { loadReplayModule } = {}
) {
  if (isInitialized) return true;

  const dsn = getTrimmedString(environment.REACT_APP_SENTRY_DSN);
  if (!dsn || environment.REACT_APP_SENTRY_ENABLED !== "true") {
    return false;
  }

  const isProduction = environment.NODE_ENV === "production";
  const integrations = [
    // Sentry defaults otherwise include console messages as breadcrumbs.
    breadcrumbsIntegration({ console: false }),
  ];

  if (isProduction) {
    integrations.unshift(
      reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    );
  }

  initSentryClient({
    dsn,
    environment: environment.NODE_ENV || "development",
    sendDefaultPii: false,
    integrations,
    tracesSampleRate: isProduction ? 1 : 0,
    replaysSessionSampleRate: isProduction ? 0.1 : 0,
    replaysOnErrorSampleRate: isProduction ? 1 : 0,
    // The backend does not participate in distributed tracing yet.
    tracePropagationTargets: [],
    initialScope: {
      tags: getTrimmedString(environment.REACT_APP_NAME)
        ? { instance: getTrimmedString(environment.REACT_APP_NAME) }
        : {},
    },
    beforeSend: sanitizeSentryEvent,
    beforeBreadcrumb: sanitizeSentryBreadcrumb,
  });

  isInitialized = true;

  if (isProduction) {
    loadReplayIntegration(loadReplayModule);
  }

  return true;
}

function getReplayIntegrationOptions() {
  return {
    maskAllText: true,
    maskAllInputs: true,
    blockAllMedia: true,
    networkDetailAllowUrls: [],
  };
}

function loadReplayIntegration(loadReplayModule = () => import(
  /* webpackChunkName: "sentry-replay" */ "@sentry/browser"
)) {
  if (replayIntegrationLoadPromise) return replayIntegrationLoadPromise;

  replayIntegrationLoadPromise = loadReplayModule()
    .then(({ replayIntegration }) => {
      addIntegration(replayIntegration(getReplayIntegrationOptions()));
    })
    .catch((error) => {
      console.error("Sentry Replay integration failed to load", error);
    });

  return replayIntegrationLoadPromise;
}

/** Keep Router instrumentation centralized and limited to the top-level route tree. */
export function withSentryReactRouterV6Routing(RoutesComponent) {
  return wrapSentryReactRouterV6Routing(RoutesComponent);
}

/** Attach only the approved authenticated identity fields to future events. */
export function setSentryUser(userData) {
  const id = userData?.id === undefined || userData?.id === null
    ? undefined
    : String(userData.id);

  if (!id) {
    clearSentryUser();
    return;
  }

  setSentryUserContext({ id });
  setTag("role", getTrimmedString(userData?.role));
}

/** Remove all authenticated context as soon as a session is no longer valid. */
export function clearSentryUser() {
  setSentryUserContext(null);
  setTag("role", undefined);
}

/** Preserve React component stacks from existing scoped class boundaries. */
export function captureSentryReactException(error, errorInfo, metadata = {}) {
  return withScope((scope) => {
    ["component", "feature"].forEach((key) => {
      const value = getTrimmedString(metadata[key]);
      if (value) scope.setTag(key, value);
    });
    return captureReactException(error, errorInfo);
  });
}

/**
 * The test page is already development-gated by its own feature flag. Keep
 * this second gate development-only so an intentional throw can never appear
 * in a production bundle's rendered UI.
 */
export function isSentrySmokeTestEnabled(environment = process.env) {
  return (
    environment.NODE_ENV === "development" &&
    environment.REACT_APP_DEMO_SHOW_TEST_PAGE === "true" &&
    environment.REACT_APP_SENTRY_TEST_BUTTON === "true"
  );
}

/** Create, but do not capture, the intentional development smoke-test error. */
export function createSentrySmokeTestError() {
  const error = new Error("Sentry frontend development smoke test");
  error.name = "SentryFrontendSmokeTestError";
  return error;
}

function SentryAppFallback({ resetError }) {
  return (
    <main
      role="alert"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: "24px",
        background: "#f7f8fa",
        color: "#1f2937",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <section style={{ maxWidth: "440px", textAlign: "center" }}>
        <h1 style={{ margin: "0 0 12px" }}>Something went wrong</h1>
        <p style={{ margin: "0 0 20px" }}>
          The application could not recover from this error. Reload the page to continue.
        </p>
        <button
          type="button"
          onClick={() => {
            if (globalThis.window?.location) {
              globalThis.window.location.reload();
              return;
            }
            resetError();
          }}
        >
          Reload application
        </button>
      </section>
    </main>
  );
}

SentryAppFallback.propTypes = {
  resetError: PropTypes.func.isRequired,
};

/** Global React 18 recovery boundary. Scoped component boundaries remain supported. */
export function SentryAppBoundary({ children }) {
  return (
    <SentryErrorBoundary fallback={SentryAppFallback}>
      {children}
    </SentryErrorBoundary>
  );
}

SentryAppBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};
